/**
 * The date pill: the site's calendar popover with a mouse, the device's own picker on touch
 * (STYLE_GUIDE "Date and time pickers", B-153).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useMediaQuery, DESKTOP_POINTER } from '../../hooks/useMediaQuery';
import PickerPopover from './PickerPopover';
import { addDays, addMonths, clampYear, formatPillDate, monthGrid, sameDay, MAX_YEAR, MIN_YEAR } from './pickerMath';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const toInput = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

interface DateFieldProps {
  value: Date;
  onChange: (date: Date) => void;
}

export const DateField: React.FC<DateFieldProps> = ({ value, onChange }) => {
  const desktop = useMediaQuery(DESKTOP_POINTER);
  const [open, setOpen] = useState(false);
  const pill = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);

  if (!desktop) {
    return (
      <div className="kg-pill flex h-[34px] items-center gap-2 rounded-[9px] px-3.5 text-[13.5px] font-semibold">
        <Calendar className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} />
        <span className="hidden sm:inline" style={{ color: 'var(--kg-faint)' }}>
          {value.toLocaleDateString('en-US', { weekday: 'short' })}
        </span>
        <input
          type="date"
          value={toInput(value)}
          onChange={(e) => {
            const [y, m, d] = e.target.value.split('-').map(Number);
            if (y && m && d) onChange(new Date(y, m - 1, d));
          }}
          className="w-[130px]"
          min="1900-01-01"
          max="2100-12-31"
          aria-label="Select date"
        />
      </div>
    );
  }

  return (
    <>
      <button
        ref={pill}
        type="button"
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => { if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); } }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Select date, ${formatPillDate(value)}`}
        className="kg-pill flex h-[34px] items-center gap-2 rounded-[9px] px-3.5 text-[13.5px] font-semibold whitespace-nowrap"
        style={open ? { borderColor: 'var(--kg-accent)' } : undefined}
      >
        <Calendar className="h-4 w-4" style={{ color: 'var(--kg-sub)' }} />
        <span style={{ color: 'var(--kg-ink)' }}>{formatPillDate(value)}</span>
      </button>
      {open && (
        <PickerPopover anchor={pill} label="Choose date" onClose={close}>
          <MonthCalendar value={value} onPick={(d) => { onChange(d); setOpen(false); pill.current?.focus(); }} />
        </PickerPopover>
      )}
    </>
  );
};

/** The month grid, with a month-and-year view for long jumps. */
const MonthCalendar: React.FC<{ value: Date; onPick: (d: Date) => void }> = ({ value, onPick }) => {
  const today = new Date();
  const [focus, setFocus] = useState(value);
  const [mode, setMode] = useState<'days' | 'months'>('days');
  const [year, setYear] = useState(value.getFullYear());
  const [yearText, setYearText] = useState(String(value.getFullYear()));
  useEffect(() => setYearText(String(year)), [year]);
  const commitYear = () => {
    const y = Number(yearText);
    setYear(Number.isFinite(y) && yearText.length === 4 ? Math.max(MIN_YEAR, Math.min(MAX_YEAR, y)) : year);
  };
  const grid = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mode === 'days') grid.current?.querySelector<HTMLElement>('[tabindex="0"]')?.focus();
  }, [focus, mode]);

  const move = (d: Date) => setFocus(clampYear(d));
  const onKey = (e: React.KeyboardEvent) => {
    const keys: Record<string, () => Date> = {
      ArrowLeft: () => addDays(focus, -1),
      ArrowRight: () => addDays(focus, 1),
      ArrowUp: () => addDays(focus, -7),
      ArrowDown: () => addDays(focus, 7),
      PageUp: () => addMonths(focus, -1),
      PageDown: () => addMonths(focus, 1),
      Home: () => addDays(focus, -focus.getDay()),
      End: () => addDays(focus, 6 - focus.getDay()),
    };
    if (keys[e.key]) {
      e.preventDefault();
      move(keys[e.key]());
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onPick(focus);
    }
  };

  const title = `${focus.toLocaleDateString('en-GB', { month: 'long' })} ${focus.getFullYear()}`;
  const arrow = 'kg-picker-icon flex h-8 w-8 items-center justify-center rounded-full';

  if (mode === 'months') {
    return (
      // Esc here steps back to the days instead of closing the whole picker.
      <div className="w-[268px]" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setMode('days'); } }}>
        <div className="mb-2 flex items-center justify-between">
          <button type="button" className={arrow} aria-label="Previous year" disabled={year <= MIN_YEAR} onClick={() => setYear((y) => Math.max(MIN_YEAR, y - 1))}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          {/* Type a year (1900 to 2100) for a long jump; Enter or leaving the field applies it. */}
          <input
            type="text"
            inputMode="numeric"
            maxLength={4}
            aria-label="Year"
            value={yearText}
            onChange={(e) => setYearText(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitYear(); } }}
            onBlur={commitYear}
            onFocus={(e) => e.target.select()}
            className="kg-picker-spin w-[72px] rounded-[8px] py-0.5 text-center font-serif text-[17px] font-semibold tabular-nums"
            style={{ color: 'var(--kg-accent)' }}
          />
          <button type="button" className={arrow} aria-label="Next year" disabled={year >= MAX_YEAR} onClick={() => setYear((y) => Math.min(MAX_YEAR, y + 1))}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS.map((m, i) => {
            const selected = year === value.getFullYear() && i === value.getMonth();
            const current = year === today.getFullYear() && i === today.getMonth();
            return (
              <button
                key={m}
                type="button"
                onClick={() => { setFocus(addMonths(new Date(year, focus.getMonth(), focus.getDate()), i - focus.getMonth())); setMode('days'); }}
                aria-current={current ? 'date' : undefined}
                className={cn('kg-picker-cell h-10 rounded-[9px] text-[13.5px] font-medium', current && !selected && 'kg-picker-today', selected && 'kg-picker-selected')}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="w-[268px]">
      <div className="mb-2 flex items-center justify-between">
        <button
          type="button"
          onClick={() => { setYear(focus.getFullYear()); setMode('months'); }}
          className="kg-picker-cell rounded-[8px] px-2 py-1 font-serif text-[17px] font-semibold"
          style={{ color: 'var(--kg-accent)' }}
          aria-label={`${title}, choose month and year`}
        >
          {title}
        </button>
        <div className="flex gap-0.5">
          <button type="button" className={arrow} aria-label="Previous month" onClick={() => move(addMonths(focus, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" className={arrow} aria-label="Next month" onClick={() => move(addMonths(focus, 1))}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div role="grid" aria-label={title} ref={grid} onKeyDown={onKey}>
        <div role="row" className="grid grid-cols-7">
          {WEEKDAYS.map((w, i) => (
            <span key={i} role="columnheader" aria-label={WEEKDAY_NAMES[i]} className="kg-picker-weekday flex h-7 items-center justify-center text-[11px] font-semibold uppercase tracking-wider">
              {w}
            </span>
          ))}
        </div>
        {[0, 1, 2, 3, 4, 5].map((row) => (
          <div role="row" key={row} className="grid grid-cols-7">
            {monthGrid(focus.getFullYear(), focus.getMonth()).slice(row * 7, row * 7 + 7).map((d) => {
              const inMonth = d.getMonth() === focus.getMonth();
              const selected = sameDay(d, value);
              const isToday = sameDay(d, today);
              return (
                <span role="gridcell" key={d.toISOString()} aria-selected={selected} className="flex items-center justify-center py-[1px]">
                  <button
                    type="button"
                    tabIndex={sameDay(d, focus) ? 0 : -1}
                    onClick={() => onPick(d)}
                    aria-label={d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                    aria-current={isToday ? 'date' : undefined}
                    className={cn(
                      'kg-picker-cell h-9 w-9 rounded-full text-[13.5px] tabular-nums',
                      !inMonth && 'kg-picker-outside',
                      isToday && !selected && 'kg-picker-today',
                      selected && 'kg-picker-selected font-semibold',
                    )}
                  >
                    {d.getDate()}
                  </button>
                </span>
              );
            })}
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-between border-t pt-2" style={{ borderColor: 'var(--kg-line)' }}>
        <button type="button" onClick={() => onPick(new Date(today.getFullYear(), today.getMonth(), today.getDate()))} className="kg-picker-link rounded-[8px] px-2 py-1 text-[13px] font-semibold">
          Today
        </button>
        <span className="self-center px-2 text-[12px]" style={{ color: 'var(--kg-faint)' }}>
          {formatPillDate(focus)}
        </span>
      </div>
    </div>
  );
};

export default DateField;
